import React, { useEffect, useMemo, useState } from "react";
import SelectMethod from "./parts/SelectMethod/SelectMethod";
import ProcessTitle from "./parts/ProcessTitle/ProcessTitle";
import CryptoForm from "./parts/CryptoForm/CryptoForm";
import {
  WithdrawalProcessSteps as Steps,
  useWithdrawalProcess,
} from "./useWithdrawalProcess";
import Loader from "@appComponents/Loader/Loader";
import Summary from "./parts/Summary/Summary";
import MfaCode from "./parts/MfaCode/MfaCode";

import { Space, Steps as StepsBase } from "antd";
import FiatForm from "./parts/FiatForm/FiatForm";
import { animateScrollTo, catchError } from "@features/utils/functions";
import WalletForm from "./parts/WalletForm/WalletForm";
import { addressRegex as addressRegexFn } from "@funds/features/helpers/validation";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import { IWithdrawFees } from "../WithdrawForm/IWithdrawForm";
import ConfirmEmail from "./parts/ConfirmEmail";

import * as styles from "./WithdrawProcessStyles.module.scss";

const PageHeaderConfig: any = {
  [Steps.SELECT_METHOD]: {
    title: "Select a withdrawal method",
  },
  [Steps.WITHDRAWAL_CRYPTO]: {
    title: "Crypto wallet withdrawal",
  },
  [Steps.WITHDRAWAL_BANK_TRANSFER]: {
    title: "Bank transfer withdrawal",
  },
  [Steps.ADD_WALLET]: {
    title: "Add New Crypto Wallet",
  },
  [Steps.WITHDRAWAL_SUMMARY]: { title: "Summary" },
  [Steps.WITHDRAWAL_VERIFICATION]: {
    title: "Verification",
  },
  [Steps.MFA_CODE]: {
    title: "Two Factor Authentication",
    description: "Enter the 2FA code from an Authenticator application",
  },
  [Steps.CONFIRM_EMAIL]: {
    title: "Check your inbox",
  },
};

type Props = {
  currency: string;
  fees: IWithdrawFees[];
};

const WithdrawProcess = ({ currency, fees }: Props) => {
  const {
    state: { step, isLoading },
    setState,
  } = useWithdrawalProcess();
  const [exchangeRate, setExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [stepLoading, setStepLoading] = useState(true);

  useEffect(() => {
    setStepLoading(true);
    SecondaryMarketService.getExchange()
      .then((res) => {
        setExchangeRate(Number(res.data[`usd_${currency.toLowerCase()}`]));
      })
      .catch((err) => {
        catchError(err);
      })
      .finally(() => {
        setStepLoading(false);
      });
  }, [currency]);

  useEffect(() => {
    if (step !== Steps.SELECT_METHOD) {
      animateScrollTo(undefined, document.getElementById("withdrawform"), 20);
    } else {
      animateScrollTo(undefined, document.getElementsByTagName("body")[0], 20);
      setState((prev) => ({
        ...prev,
        selectedWithdrawal: undefined,
        amount: "",
      }));
    }
  }, [step]);

  const stepMapper = useMemo(() => {
    switch (step) {
      case Steps.SELECT_METHOD:
        return 0;
      case Steps.WITHDRAWAL_CRYPTO:
      case Steps.WALLET_FORM:
      case Steps.WITHDRAWAL_BANK_TRANSFER:
        return 0;
      case Steps.WITHDRAWAL_SUMMARY:
        return 1;
      case Steps.MFA_CODE:
        return 2;
      case Steps.CONFIRM_EMAIL:
        return 2;
      default:
        return 0;
    }
  }, [step]);

  const renderStep = useMemo(() => {
    switch (step) {
      case Steps.SELECT_METHOD:
        return (
          <SelectMethod
            fees={fees}
            exchangeRate={exchangeRate}
            currency={currency}
          />
        );
      case Steps.ADD_WALLET: {
        const addressRegex = addressRegexFn(fees, currency);
        return <WalletForm addressRegex={addressRegex} currency={currency} />;
      }
      case Steps.WITHDRAWAL_CRYPTO:
        return (
          <CryptoForm
            currency={currency}
            fees={fees}
            exchangeRate={exchangeRate}
          />
        );
      case Steps.WITHDRAWAL_SUMMARY:
        return <Summary fees={fees} />;
      case Steps.MFA_CODE:
        return <MfaCode />;
      case Steps.WITHDRAWAL_BANK_TRANSFER: {
        return <FiatForm currency={currency} fees={fees} />;
      }
      case Steps.CONFIRM_EMAIL: {
        return <ConfirmEmail />;
      }
      default:
      case Steps.WITHDRAWAL_CRYPTO:
        return (
          <CryptoForm
            currency={currency}
            fees={fees}
            exchangeRate={exchangeRate}
          />
        );
    }
  }, [step, fees, exchangeRate]);

  const stepPercentMapper = useMemo(() => {
    switch (step) {
      case Steps.SELECT_METHOD:
        return 0;
      case Steps.WITHDRAWAL_CRYPTO:
        return 0;
      case Steps.WALLET_FORM:
        return 50;
      case Steps.WITHDRAWAL_SUMMARY:
        return undefined;
      case Steps.MFA_CODE:
        return undefined;
      case Steps.WITHDRAWAL_BANK_TRANSFER:
        return 0;
      case Steps.ADD_WALLET:
        return 50;
      default:
        return undefined;
    }
  }, [step]);

  if (isLoading || stepLoading)
    return (
      <div className={styles.loaderWrapper}>
        <Loader />
      </div>
    );
  const { Step } = StepsBase;

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <div style={{ borderBottom: "1px solid #e4e4e4", paddingBottom: "10px" }}>
        <StepsBase
          current={stepMapper}
          labelPlacement="vertical"
          percent={stepPercentMapper}
          className={styles.steps}
        >
          <Step title="Account" />
          <Step title="Summary" />
          <Step title="Verification" />
        </StepsBase>
      </div>
      <ProcessTitle {...PageHeaderConfig[step]}>{renderStep}</ProcessTitle>
    </Space>
  );
};

export default WithdrawProcess;
